import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { MaskedLines } from "@/components/MaskedLines";
import { api } from "@/lib/api";
import { scrollToHash } from "@/lib/scroll";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { useConfig } from "@/context/ConfigContext";
import { inr } from "@/lib/format";
import { TID } from "@/lib/testIds";

const HERO_IMG =
  "https://images.unsplash.com/photo-1735948055457-8d816fb80a87?crop=entropy&cs=srgb&fm=jpg&q=85";

const STEPS = [
  {
    n: "01",
    t: "Direct & define",
    d: "We map your goals, your audience and the one job your website must do — before a single pixel moves.",
  },
  {
    n: "02",
    t: "Design & engineer",
    d: "Bespoke visual direction, built with motion and speed as first-class features, never afterthoughts.",
  },
  {
    n: "03",
    t: "Launch & refine",
    d: "We ship, measure and refine — then maintenance keeps the craft sharp, month after month.",
  },
];

const MARQUEE_ITEMS = [
  "STRATEGIC DESIGN",
  "PRECISION ENGINEERING",
  "SEO · AEO · GEO",
  "CONVERSION-FIRST BUILDS",
  "MOTION & INTERACTION",
  "MAINTAINED AFTER LAUNCH",
];

export default function Home() {
  const { config, loading } = useConfig();
  const pkgs = config.packages || [];
  const ess = pkgs.find((p) => p.id === "essential") || pkgs[0];
  const minMaint = pkgs.length ? Math.min(...pkgs.map((p) => p.maintenance || 0)) : 0;
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
  }, []);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const t = setTimeout(() => scrollToHash(location.hash), 400);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <main>
      <Seo
        title="OFFCRAFT — Premium Website Design & Development"
        description="OFFCRAFT designs and builds premium, conversion-focused websites for growing businesses — clear packages, honest pricing and defined monthly maintenance."
      />
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden border-b border-ink/10">
        <motion.div style={{ y: gridY }} className="grid-bg pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 pb-20 pt-28 lg:grid-cols-12 lg:pb-28 lg:pt-40">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-crimson"
            >
              {config.brand_name || "OFFCRAFT"} — Web design studio
            </motion.p>
            <MaskedLines
              ariaLabel="Websites built like craft"
              delay={0.35}
              className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl"
              lines={[<>Websites built</>, <>like <span className="text-crimson">craft.</span></>]}
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.7 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-ink/60 sm:text-lg"
            >
              Considered design and clean engineering for businesses that have outgrown templates.
              {ess ? (
                <>
                  {" "}Packages from <span className="font-semibold text-ink" data-testid={TID.home.heroPrice}>{inr(ess.dev_fee)}</span> one-time,
                  with maintenance from {inr(minMaint)}/month.
                </>
              ) : null}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                data-testid={TID.home.heroCta}
                to="/pricing"
                className="group inline-flex items-center gap-2 bg-crimson px-8 py-4 font-display text-sm font-bold text-white transition-colors hover:bg-crimsondark"
              >
                View packages
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
              <Link
                data-testid={TID.home.heroContact}
                to="/contact"
                className="inline-flex items-center gap-2 border border-ink/10 px-8 py-4 font-display text-sm font-bold text-ink transition-colors hover:border-forest/60 hover:text-crimson"
              >
                Start a project
              </Link>
            </motion.div>
          </div>
          <div className="relative lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-0 translate-x-4 translate-y-4 border border-crimson/30" aria-hidden="true" />
              <div className="relative overflow-hidden">
                <motion.img
                  initial={{ scale: 1.08, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 1.2, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                  src={HERO_IMG}
                  alt="OFFCRAFT studio work"
                  className="h-[380px] w-full object-cover lg:h-[520px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 border border-white/20 bg-black/55 px-3 py-2 backdrop-blur-sm">
                  <span className="inline-block h-1.5 w-1.5 rotate-45 bg-crimson" />
                  <span className="font-mono text-[11px] font-medium tracking-[0.18em] text-white">SELECTED WORK — CRAFTED IN-HOUSE</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        {!loading && pkgs.length > 0 && (
          <div className="relative border-t border-ink/10">
            <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-forest/5 px-6 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <div className="py-5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60">Packages — {String(pkgs.length).padStart(2, "0")}</div>
              <div className="py-5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60">From {ess ? inr(ess.dev_fee) : "—"} one-time</div>
              <div className="py-5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60">Maintenance from {inr(minMaint)}/mo</div>
            </div>
          </div>
        )}
      </section>

      <section className="border-b border-ink/10 bg-surface/60">
        <div className="mx-auto max-w-7xl px-6 py-14 text-center">
          <Reveal>
            <p data-testid="home-mission-line" className="font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Websites built to move your business <span className="text-crimson">forward.</span>
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} data-testid={TID.home.marquee} />

      {/* HOW WE WORK */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:py-32">
        <Reveal>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-crimson">How we work</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            A process with no wasted motion.
          </h2>
        </Reveal>
        <div className="mt-14 border-t border-forest/10">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="group grid gap-4 border-b border-forest/10 py-8 transition-colors hover:bg-crimson/[0.02] sm:grid-cols-12 sm:items-center sm:gap-8">
                <span className="font-mono text-xs tracking-[0.3em] text-crimson sm:col-span-1">{s.n}</span>
                <h3 className="font-display text-2xl font-bold text-ink transition-colors group-hover:text-crimson sm:col-span-4">{s.t}</h3>
                <p className="text-sm leading-relaxed text-ink/55 sm:col-span-7">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PACKAGES PREVIEW */}
      <section className="border-t border-ink/10 bg-ink/[0.03]">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-crimson">Packages & pricing</p>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">Three packages. One honest price.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/pricing" className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-crimson transition-colors hover:text-crimsonlight">
                Full comparison →
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-px border border-forest/10 bg-crimson/10 sm:grid-cols-3">
            {pkgs.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className="h-full">
                <Link
                  to={`/contact?package=${p.id}`}
                  data-testid={TID.home.mini(p.id)}
                  className="group flex h-full min-h-[240px] flex-col justify-between bg-paper p-6 transition-colors hover:bg-surface sm:p-8"
                >
                  <div>
                    <div className="font-display text-sm font-extrabold uppercase tracking-[0.25em] text-crimson">PACKAGE {p.number}</div>
                    <div className="mt-2 font-display text-2xl font-bold text-ink transition-colors group-hover:text-crimson">{p.name}</div>
                    <div className="mt-1 text-xs leading-relaxed text-ink/60">{p.ideal_for}</div>
                  </div>
                  <div className="mt-8">
                    <div className="font-display text-3xl font-extrabold text-ink">
                      {p.price_prefix ? `${p.price_prefix} ` : ""}
                      {inr(p.dev_fee)}
                    </div>
                    <div className="mt-1 font-mono text-[11px] tracking-[0.2em] text-ink/60">+ {inr(p.maintenance)}/MO MAINTENANCE</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          {!loading && pkgs.length === 0 && (
            <p className="mt-8 font-mono text-xs text-ink/60">Packages are being updated — check back shortly.</p>
          )}
        </div>
      </section>

            {/* TESTIMONIALS */}
      {!loading && testimonials.length > 0 && (
        <section data-testid="testimonials-section" className="border-t border-ink/10 bg-ink/[0.03]">
          <div className="mx-auto max-w-7xl px-6 py-24 lg:py-28">
            <Reveal>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-crimson">Client words</p>
              <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">People who shipped with us.</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {testimonials.slice(0, 6).map((t, i) => (
                <Reveal key={t._id} delay={i * 0.06} className="h-full">
                  <figure data-testid={`testimonial-card-${t._id}`} className="flex h-full flex-col border border-ink/10 bg-surface p-6 sm:p-8">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: t.rating }).map((_, st) => (
                        <Star key={st} className="h-4 w-4 fill-crimson text-crimson" />
                      ))}
                    </div>
                    <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-ink/75">"{t.quote}"</blockquote>
                    <figcaption className="mt-6 flex items-center gap-3 border-t border-ink/10 pt-4">
                      {t.photo ? (
                        <img src={t.photo} alt={t.name} className="h-11 w-11 rounded-full border border-ink/10 object-cover" />
                      ) : (
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink/[0.06] font-display text-sm font-bold text-crimson">
                          {(t.name || "?").trim()[0].toUpperCase()}
                        </span>
                      )}
                      <span>
                        <span className="block font-display text-sm font-bold text-ink">{t.name}</span>
                        {t.role ? <span className="block text-xs text-ink/55">{t.role}</span> : null}
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CLOSING CTA */}
      <section className="border-t border-ink/10 bg-surface/60">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center lg:py-24">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-crimson">Ready when you are</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Let's build something worth launching.
            </h2>
            <Link
              data-testid={TID.home.ctaBand}
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-crimson px-8 py-4 font-display text-sm font-bold text-white transition-colors hover:bg-crimsondark"
            >
              Start a project
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
